import { useEffect } from "react";

import dayjs from "dayjs";

import { ACTIVITIES_SELECTION_RELEASE } from "@/assignments/constants/activitySelectionReleaseDate";
import { handleUpdateActivitiesSelection } from "@/assignments/utils/assignmentActions";
import getAttachmentsFilteredByLicense from "@/assignments/utils/getAttachmentsFilteredByLicense";

interface Attachment {
  id: string;
}
interface UserProperties {
  canViewPowerWords: boolean;
}

interface UseSetAssignmentActivitiesProps {
  assignmentModifiedDate: string;
  attachmentsByLanguage: Attachment[];
  dispatch: () => void;
  hasUnsavedChanges: boolean;
  isSavedAssignment: boolean;
  userProperties: UserProperties;
}

const useSetAssignmentActivities = ({
  assignmentModifiedDate,
  attachmentsByLanguage,
  dispatch,
  hasUnsavedChanges,
  isSavedAssignment,
  userProperties,
}: UseSetAssignmentActivitiesProps) => {
  const { canViewPowerWords } = userProperties;
  // Filter out ChecksForUnderstanding & GuidedHighlighting quizzes from attachments since they are assigned
  // with assignment_config rather than activity_members/activity selection
  const canViewChecksForUnderstanding = false;
  const canViewGuidedHighlighting = false;
  const filteredAttachments = getAttachmentsFilteredByLicense(
    attachmentsByLanguage,
    canViewPowerWords,
    canViewChecksForUnderstanding,
    canViewGuidedHighlighting,
  );

  const modifiedDate = dayjs(assignmentModifiedDate);
  const modifiedBeforeActivitySelectionFeature = modifiedDate.isBefore(
    dayjs(ACTIVITIES_SELECTION_RELEASE),
  );

  useEffect(() => {
    const attachmentIds =
      filteredAttachments?.map((attachment: Attachment) => attachment.id) || [];

    /**
     * Check for !hasUnsavedChanges with unsaved assignments so that activity
     * selections are not reset when the activity panel is closed and reopened.
     */
    if (
      (!isSavedAssignment && !hasUnsavedChanges) ||
      modifiedBeforeActivitySelectionFeature
    ) {
      // Add all activities to new assignments and those created before the activity selection feature release.
      handleUpdateActivitiesSelection(
        { attachmentIds, action: "add" },
        dispatch,
      );
    }
  }, [attachmentsByLanguage?.length]);
};

export default useSetAssignmentActivities;
